import { z } from 'zod';
import { AREAS } from '../constants/areas.js';

export const userRoleSchema = z.enum(['admin', 'user', 'ceo', 'viewer']);
export type UserRole = z.infer<typeof userRoleSchema>;

/**
 * Write-side area validator — accepts only the official AREAS (or null).
 * Legacy reads stay permissive via `userSchema.area: z.string().nullable()`.
 */
const writeAreaSchema = z.enum(AREAS).nullable();

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string().nullable().default(null),
  role: userRoleSchema.default('user'),
  area: z.string().nullable().default(null),
  isActive: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string().email().nullable().default(null),
  // Metric Hub reviewer flag — only DATA-area users with this set to true
  // can approve/reject metric drafts. Admin-settable. Legacy docs without
  // the field rehydrate to `false` on read.
  reviewer: z.boolean().optional().default(false),
  // Slack DM mirror opt-out. When true (default) the user receives Metric Hub
  // notifications as Slack DMs in addition to the in-app bell. User-editable
  // from their profile. Legacy docs rehydrate to `true`.
  slackNotificationsEnabled: z.boolean().optional().default(true),
  // Cached Slack user id (resolved once via users.lookupByEmail, then reused)
  // so the DM fan-out doesn't hit the Slack lookup API on every send. System-
  // managed — never user- or admin-editable. `null` = not resolved yet.
  slackUserId: z.string().nullable().optional().default(null),
});
export type User = z.infer<typeof userSchema>;

/**
 * Legacy-tolerant read schema. The GAS frontend only wrote `updatedAt` on
 * edits, never on initial create — so docs migrated from that era are missing
 * `updatedAt` and would otherwise fail safeParse and silently disappear from
 * `GET /users`. We fill the gap from `createdAt` and let `createdBy` be null
 * when absent. Writes (createUserSchema / updateUserSchema) stay strict so we
 * never produce these gaps for new docs.
 */
export const userReadSchema = z.preprocess((raw) => {
  if (typeof raw !== 'object' || raw === null) return raw;
  const obj = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };
  if (out.updatedAt === undefined && typeof out.createdAt === 'string') {
    out.updatedAt = out.createdAt;
  }
  if (out.createdBy === undefined) {
    out.createdBy = null;
  }
  return out;
}, userSchema);

export const createUserSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1).optional(),
  role: userRoleSchema.default('user'),
  area: writeAreaSchema.optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  displayName: z.string().min(1).optional(),
  role: userRoleSchema.optional(),
  area: writeAreaSchema.optional(),
  isActive: z.boolean().optional(),
  reviewer: z.boolean().optional(),
  slackNotificationsEnabled: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/**
 * Self-edit input — what a non-admin user can change about themselves. Same
 * area allowlist as admin writes; role/isActive omitted on purpose.
 */
export const updateSelfSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  area: writeAreaSchema.optional(),
  slackNotificationsEnabled: z.boolean().optional(),
});
export type UpdateSelfInput = z.infer<typeof updateSelfSchema>;

/**
 * Lite user shape — what `GET /users/lite` returns for non-admin callers.
 * Kept intentionally tiny: it's the minimum a sharing picker needs (display
 * name, email, area badge). No timestamps, no role, no createdBy.
 */
export const userLiteSchema = z.object({
  email: z.string().email(),
  displayName: z.string().nullable(),
  area: z.string().nullable(),
});
export type UserLite = z.infer<typeof userLiteSchema>;
